import styled, { css } from 'styled-components';
import React from 'react';
import { withCssSelector } from '../shared/withCssSelector';
import { PropsWithClassName } from '../tabselector/PropsWithClassName';
import { CardOption, CardOptionOrientation, CardOptionSize } from './CardOption';

const StyledSelector = styled.div<{ $size?: CardOptionSize }>`
	display: flex;
	flex-direction: row;
	gap: 8px;

	${({ $size }) => $size === 'large' && css`
		gap: 16px;
	`}
`;

type CardOptionDefinition = {
	value: string;
	label: string | React.ReactNode;
	/** Second line under the label, which turns the label into a title. */
	description?: string | React.ReactNode;
	graphic?: React.ReactNode;
	/** Full-bleed picture above the label. */
	image?: string;
	/** Dimmed, unselectable, and skipped by the keyboard. */
	disabled?: boolean;
}

export type CardSelectorProps = {
	options: CardOptionDefinition[];
	selectedId?: string;
	onSelected: (value: string) => void;
	/** `horizontal` puts each card's graphic beside its label. */
	orientation?: CardOptionOrientation;
	/** `large` spreads the cards across the full width and packs them top-left. */
	size?: CardOptionSize;
}

export const CardSelector = withCssSelector(React.forwardRef<HTMLDivElement, PropsWithClassName<CardSelectorProps>>(
	function CardSelector({
		className,
		options,
		selectedId,
		onSelected,
		orientation = 'vertical',
		size = 'default'
	}, ref) {
		return <StyledSelector
			ref={ref}
			className={className}
			role="radiogroup"
			$size={size}
		>
			{options.map(option => <CardOption
				key={option.value}
				{...option}
				orientation={orientation}
				size={size}
				selected={option.value === selectedId}
				onSelected={onSelected} />
			)}
		</StyledSelector>;
	}
), StyledSelector.toString());
